import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  format,
  startOfMonth,
  getDay,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Trash2,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Edit2,
} from "lucide-react";
import {
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  collection,
  where,
  query,
  getDocs,
  collectionGroup,
} from "firebase/firestore";
import { useAuth } from "../hooks/AuthenticationProvider";
import { db } from "../utils/firebase";

const noop = () => {};

// Initial state
const defaultEventState = {
  title: "",
  type: "House",
  location: { name: "", address: "" },
  startDate: new Date(),
  endDate: new Date(), // Same day by default
  startTime: "09:00",
  endTime: "10:00",
  dayOfWeek: new Date().getDay(),
  frequency: null,
  exceptions: {},
  hasAttachedForm: false,
  attachedFormId: null,
  attachedFormName: null,
};

const Calendar = ({
  isCompact = false,
  onAddEvent = noop, // <- default to no-op
  onUpdateEvent = noop,
  onDeleteEvent = noop,
  onOpenForm,
  meetings = [],
}) => {
  const { user, organizationId, isLoading, checkPermission } = useAuth();
  const location = useLocation();
  const isResidentDashboard = location.pathname === "/residentdashboard";

  const canManageSchedule = checkPermission("updateSchedule");

  // Core state
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(defaultEventState);
  const [events, setEvents] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isVisible, setIsVisible] = useState(true);
  const [viewingSubmissions, setViewingSubmissions] = useState(null);
  const [selectedDateSubmissions, setSelectedDateSubmissions] = useState([]);
  const [expandedSubmissions, setExpandedSubmissions] = useState({});
  const [residentMeetingCounts, setResidentMeetingCounts] = useState({});

  const toggleSubmission = (index) => {
    setExpandedSubmissions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // UI state
  const [showDayModal, setShowDayModal] = useState(false);
  const [showEventFormModal, setShowEventFormModal] = useState(false);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedDateCheckIns, setSelectedDateCheckIns] = useState([]);
  const [showDeleteRecurringConfirm, setShowDeleteRecurringConfirm] =
    useState(false);
  const [overnightRequests, setOvernightRequests] = useState([]);
  const [selectedDateOvernights, setSelectedDateOvernights] = useState([]);
  const [showRecurringConfirm, setShowRecurringConfirm] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);
  const [availableForms, setAvailableForms] = useState([]);

  const isLastDayOfType = (date, dayOfWeek) => {
    const testDate = new Date(date);
    const month = testDate.getMonth();

    // Move to the next week
    testDate.setDate(testDate.getDate() + 7);

    // If we're in a new month, then the original date was the last occurrence
    return testDate.getMonth() !== month;
  };

  // Updated fetch logic for check-ins in Calendar component
  useEffect(() => {
    if (!organizationId || !isVisible) return;

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    const eventsRef = collection(
      db,
      `organizations/${organizationId}/schedule`
    );

    // Queries
    const eventsQuery = query(eventsRef);

    // Fetch Events
    const unsubscribeEvents = onSnapshot(eventsQuery, async (snapshot) => {
      const fetchedEvents = snapshot.docs.map((doc) => {
        const eventData = doc.data();
        return {
          id: doc.id,
          ...eventData,
          startDate:
            eventData.startDate?.toDate?.() || new Date(eventData.startDate),
          endDate: eventData.endDate?.toDate?.() || new Date(eventData.endDate),
        };
      });

      // Expand recurring events BEFORE filtering
      const allOccurrences = fetchedEvents.flatMap((event) =>
        generateOccurrences(event, monthStart, monthEnd)
      );

      // Now filter occurrences to show only this month's
      const visibleOccurrences = allOccurrences.filter((occurrence) => {
        const occurrenceDate = new Date(occurrence.date);
        return occurrenceDate >= monthStart && occurrenceDate <= monthEnd;
      });

      setEvents(visibleOccurrences);
    });

    // Fetch Check-ins from all residents and calculate weekly meeting counts
    const fetchAllCheckIns = async () => {
      try {
        // Define date range for the query
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);

        console.log(
          `Fetching check-ins for period: ${monthStart} to ${monthEnd}`
        );

        // Query from the organization-wide check-ins collection
        const checkInsRef = collection(
          db,
          `organizations/${organizationId}/checkIns`
        );

        const checkInsQuery = query(
          checkInsRef,
          where("timestamp", ">=", monthStart),
          where("timestamp", "<=", monthEnd)
        );

        const checkInsSnapshot = await getDocs(checkInsQuery);

        const allCheckIns = checkInsSnapshot.docs.map((doc) => {
          const data = doc.data();
          const checkInTime = data.timestamp?.toDate();
          const checkOutTime = data.checkOutTime?.toDate
            ? data.checkOutTime.toDate()
            : data.checkOutTime
            ? new Date(data.checkOutTime)
            : null;

          // Calculate duration and determine if it meets minimum requirement
          let duration = null;
          let meetsMinimumDuration = false;

          if (checkOutTime && checkInTime) {
            duration =
              (checkOutTime.getTime() - checkInTime.getTime()) / (1000 * 60); // duration in minutes

            // Debug logging
            console.log(
              `Check-in for ${
                data.residentName
              }: Duration = ${duration} minutes, Meets minimum? ${
                duration >= 45
              }`
            );

            meetsMinimumDuration = duration >= 45; // 45 minute minimum
          } else if (data.duration !== undefined && data.duration !== null) {
            // If duration is already calculated and stored in the document
            duration = data.duration;
            meetsMinimumDuration = duration >= 45;
            console.log(
              `Using stored duration for ${
                data.residentName
              }: ${duration} minutes, Meets minimum? ${duration >= 45}`
            );
          } else {
            // No checkout time and no stored duration - still active or incomplete
            console.log(
              `No checkout time for ${data.residentName}, treating as incomplete`
            );
            meetsMinimumDuration = false;
          }

          return {
            id: doc.id,
            ...data,
            timestamp: checkInTime,
            checkOutTime,
            hasCheckOut: !!checkOutTime,
            duration,
            meetsMinimumDuration, // Add this flag
          };
        });

        console.log(`Retrieved ${allCheckIns.length} check-ins`);
        setCheckIns(allCheckIns);

        // Calculate weekly meeting counts for each resident - ONLY count meetings that meet minimum duration
        const weeklyMeetingCounts = {};

        // Get the start and end of the current week
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, etc.

        // Get the date of the start of this week (Sunday)
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - dayOfWeek);
        startOfWeek.setHours(0, 0, 0, 0);

        // Get the date of the end of this week (Saturday)
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        // Filter for check-ins this week, type="meeting", AND meets minimum duration
        const weeklyMeetingCheckIns = allCheckIns.filter((checkIn) => {
          const timestamp = checkIn.timestamp;
          const meetsRequirements =
            timestamp >= startOfWeek &&
            timestamp <= endOfWeek &&
            checkIn.type === "meeting" &&
            checkIn.meetsMinimumDuration;

          if (checkIn.type === "meeting") {
            console.log(
              `${checkIn.residentName}: Duration ${checkIn.duration}min, Meets minimum? ${checkIn.meetsMinimumDuration}, Counts for week? ${meetsRequirements}`
            );
          }

          return meetsRequirements;
        });

        // Count meetings per resident (one per day)
        weeklyMeetingCheckIns.forEach((checkIn) => {
          const residentId = checkIn.residentId;
          if (!residentId) return;

          // Convert timestamp to date string (YYYY-MM-DD)
          const checkInDate = checkIn.timestamp.toISOString().split("T")[0];

          // Initialize tracking for this resident if needed
          if (!weeklyMeetingCounts[residentId]) {
            weeklyMeetingCounts[residentId] = {
              count: 0,
              dates: new Set(),
            };
          }

          // Only count one meeting per day (and only if it meets minimum duration)
          if (!weeklyMeetingCounts[residentId].dates.has(checkInDate)) {
            weeklyMeetingCounts[residentId].dates.add(checkInDate);
            weeklyMeetingCounts[residentId].count++;
          }
        });

        // Update the check-in objects with their weekly meeting count
        const updatedCheckIns = allCheckIns.map((checkIn) => {
          if (checkIn.type === "meeting" && checkIn.residentId) {
            return {
              ...checkIn,
              weeklyMeetingCount:
                weeklyMeetingCounts[checkIn.residentId]?.count || 0,
            };
          }
          return checkIn;
        });

        setCheckIns(updatedCheckIns);
      } catch (error) {
        console.error("Error fetching check-ins:", error);
        setCheckIns([]);
      }
    };

    // Call the function to fetch check-ins
    fetchAllCheckIns();

    const overnightRequestsRef = collection(
      db,
      `organizations/${organizationId}/overnightRequests`
    );

    // Queries
    const overnightRequestsQuery = query(
      overnightRequestsRef,
      where("status", "==", "approved"),
      where("archived", "==", false)
    );

    // Fetch Overnight Requests
    const unsubscribeOvernights = onSnapshot(
      overnightRequestsQuery,
      (snapshot) => {
        setOvernightRequests(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            startDate: new Date(doc.data().startDate),
            endDate: new Date(doc.data().endDate),
          }))
        );
      }
    );

    return () => {
      unsubscribeEvents();
      unsubscribeOvernights();
    };
  }, [currentMonth, organizationId, isVisible]);

  const getWeeklyMeetingCounts = async () => {
    if (!organizationId) return {};

    try {
      // Calculate the start and end of the current week
      const today = new Date();
      const weekStart = startOfWeek(today);
      const weekEnd = endOfWeek(today);

      // Get all check-ins for the week from the organization-wide collection
      const checkInsRef = collection(
        db,
        `organizations/${organizationId}/checkIns`
      );

      const checkInsQuery = query(
        checkInsRef,
        where("timestamp", ">=", weekStart),
        where("timestamp", "<=", weekEnd),
        where("type", "==", "meeting")
      );

      const snapshot = await getDocs(checkInsQuery);

      // Create an object to track meeting days for each resident
      const residentMeetingDays = {};

      snapshot.docs.forEach((doc) => {
        const checkIn = doc.data();
        const residentId = checkIn.residentId;

        if (!residentId) return;

        // Convert timestamp to date string in YYYY-MM-DD format
        const timestamp = checkIn.timestamp.toDate
          ? checkIn.timestamp.toDate()
          : new Date(checkIn.timestamp);

        const dateString = timestamp.toISOString().split("T")[0];

        // Initialize tracking for this resident if needed
        if (!residentMeetingDays[residentId]) {
          residentMeetingDays[residentId] = new Set();
        }

        // Add this day to the resident's set of meeting days
        residentMeetingDays[residentId].add(dateString);
      });

      // Convert sets to counts
      const counts = {};
      for (const [residentId, daysSet] of Object.entries(residentMeetingDays)) {
        counts[residentId] = daysSet.size;
      }

      return counts;
    } catch (error) {
      console.error("Error calculating weekly meeting counts:", error);
      return {};
    }
  };

  // Replace the current getDayOvernights function with this updated version
  // Replace the current getDayOvernights function with this updated version
  const getDayOvernights = (date) => {
    return overnightRequests.filter((overnight) => {
      try {
        // Validate that overnight data exists and has required fields
        if (!overnight || !overnight.startDate || !overnight.endDate) {
          console.warn("Invalid overnight request data:", overnight);
          return false;
        }

        // Get date objects from overnight data with validation
        const startDateObj = new Date(overnight.startDate);
        const endDateObj = new Date(overnight.endDate);
        const checkDateObj = new Date(date);

        // Validate that all dates are valid
        if (
          isNaN(startDateObj.getTime()) ||
          isNaN(endDateObj.getTime()) ||
          isNaN(checkDateObj.getTime())
        ) {
          console.warn("Invalid date values in overnight request:", {
            startDate: overnight.startDate,
            endDate: overnight.endDate,
            checkDate: date,
          });
          return false;
        }

        // Create date-only strings for comparison (YYYY-MM-DD)
        const startDateStr = startDateObj.toISOString().split("T")[0];
        const endDateStr = endDateObj.toISOString().split("T")[0];
        const checkDateStr = checkDateObj.toISOString().split("T")[0];

        // Convert all to Date objects with time set to midnight for proper comparison
        const startDateMidnight = new Date(startDateStr + "T00:00:00");
        const endDateMidnight = new Date(endDateStr + "T00:00:00");
        const checkDateMidnight = new Date(checkDateStr + "T00:00:00");

        // For overnight on 4/4-4/5, show it on 4/4 only
        const result =
          checkDateMidnight >= startDateMidnight &&
          checkDateMidnight < endDateMidnight;

        console.log(`Show on ${checkDateStr}? ${result}`);

        return result;
      } catch (error) {
        console.error("Error processing overnight request:", error, overnight);
        return false;
      }
    });
  };

  const getDayCheckIns = (date) => {
    return checkIns.filter((checkIn) => {
      const checkInDate = new Date(checkIn.timestamp);
      return (
        checkInDate.getDate() === date.getDate() &&
        checkInDate.getMonth() === date.getMonth() &&
        checkInDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Helper to generate occurrences for a recurring event
  const generateOccurrences = (recurringEvent, start, end) => {
    if (!recurringEvent || !start || !end) {
      console.warn("Missing required parameters in generateOccurrences");
      return [];
    }

    const occurrences = [];
    const addedDates = new Set(); // Track unique dates

    const getDateValue = (dateField) => {
      if (!dateField) return null;
      const date = dateField.toDate?.() || new Date(dateField);
      return isNaN(date.getTime()) ? null : date;
    };

    const eventStart = getDateValue(recurringEvent.startDate);
    if (!eventStart) {
      console.warn("Invalid start date in recurringEvent");
      return [];
    }

    const targetDayOfWeek = recurringEvent.dayOfWeek;
    const endDate = new Date(
      Math.min(
        getDateValue(recurringEvent.endDate)?.getTime() || end.getTime(),
        end.getTime()
      )
    );

    // For bi-weekly, calculate all occurrences up front
    if (recurringEvent.frequency === "biweekly") {
      let current = new Date(eventStart);
      let iterationCount = 0;
      const MAX_ITERATIONS = 1000; // Safety limit

      while (current <= endDate && iterationCount < MAX_ITERATIONS) {
        const dateStr = format(current, "yyyy-MM-dd");

        if (
          current >= start &&
          current <= endDate &&
          !addedDates.has(dateStr)
        ) {
          addedDates.add(dateStr);
          const occurrence = {
            ...recurringEvent,
            date: new Date(current),
            startTime: recurringEvent.startTime,
            endTime: recurringEvent.endTime,
            title: recurringEvent.title,
            location: recurringEvent.location,
          };

          const exception = recurringEvent.exceptions?.[dateStr];

          if (!exception?.cancelled) {
            occurrences.push({
              ...occurrence,
              startTime: exception?.modified?.startTime || occurrence.startTime,
              endTime: exception?.modified?.endTime || occurrence.endTime,
              title: exception?.modified?.title || occurrence.title,
              location: exception?.modified?.location || occurrence.location,
              isException: !!exception,
            });
          }
        }
        // Add exactly 14 days using milliseconds
        current = new Date(current.getTime() + 14 * 24 * 60 * 60 * 1000);
        iterationCount++;
      }

      if (iterationCount >= MAX_ITERATIONS) {
        console.warn("Max iterations reached in bi-weekly generation");
      }

      return occurrences;
    }

    // Handle weekly and monthly events
    let currentDate = new Date(Math.max(eventStart.getTime(), start.getTime()));
    let iterationCount = 0;
    const MAX_ITERATIONS = 1000;

    // For weekly events, adjust to first occurrence if we're starting mid-week
    if (recurringEvent.frequency === "weekly") {
      while (
        currentDate.getDay() !== targetDayOfWeek &&
        iterationCount < MAX_ITERATIONS
      ) {
        currentDate.setDate(currentDate.getDate() + 1);
        iterationCount++;
      }
    }

    // For monthly events, we need special handling to find the correct day
    // For monthly events, we need special handling to find the correct day
    if (recurringEvent.frequency === "monthly") {
      const originalDate = new Date(eventStart);

      // Check if this is a "last of month" event
      const isLastOfMonth = isLastDayOfType(originalDate, targetDayOfWeek);

      if (isLastOfMonth) {
        // Handle "last of month" recurrence
        // Find the last occurrence of this day in the current month
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        // Start from the last day of the month and work backward
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const lastDayOfWeek = lastDay.getDay();

        // Calculate days to go back to find the last targetDayOfWeek
        let daysToSubtract = (lastDayOfWeek - targetDayOfWeek + 7) % 7;
        if (daysToSubtract === 0 && lastDayOfWeek === targetDayOfWeek) {
          daysToSubtract = 0; // It's already the right day
        }

        // Set to the last occurrence of target day in this month
        currentDate = new Date(lastDay);
        currentDate.setDate(lastDay.getDate() - daysToSubtract);
      } else {
        // Use the existing "nth week of month" logic for non-last-of-month events
        const firstDayOfOriginalMonth = new Date(
          originalDate.getFullYear(),
          originalDate.getMonth(),
          1
        );
        // Calculate the day of the week of the first day of the month (0-6)
        const firstDayWeekday = firstDayOfOriginalMonth.getDay();

        // Calculate what week of the month the event falls on
        const originalDay = originalDate.getDate();
        const originalWeekdayPosition = Math.ceil(
          (originalDay +
            firstDayWeekday -
            ((7 + targetDayOfWeek - firstDayWeekday) % 7)) /
            7
        );

        // Now adjust currentDate to the correct month and the correct instance of that day of week
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        // Find the first occurrence of targetDayOfWeek in the current month
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        const daysUntilFirst =
          (7 + targetDayOfWeek - firstDayOfMonth.getDay()) % 7;
        let targetDate = new Date(
          currentYear,
          currentMonth,
          1 + daysUntilFirst
        );

        // Now move forward to the correct week of the month
        targetDate.setDate(
          targetDate.getDate() + (originalWeekdayPosition - 1) * 7
        );

        // If this date is valid and within our range, use it
        if (targetDate >= start && targetDate <= endDate) {
          currentDate = targetDate;
        } else {
          // If we've gone past our range, move to the next month
          currentDate.setMonth(currentDate.getMonth() + 1);
          currentDate.setDate(1);
        }
      }
    }

    iterationCount = 0; // Reset for main loop

    while (currentDate <= endDate && iterationCount < MAX_ITERATIONS) {
      let shouldInclude = false;
      const dateStr = format(currentDate, "yyyy-MM-dd");

      switch (recurringEvent.frequency) {
        case "weekly":
          shouldInclude = currentDate.getDay() === targetDayOfWeek;
          break;
        case "monthly":
          // For monthly, we've already positioned the date correctly before entering the loop
          shouldInclude = true;
          break;
        default:
          shouldInclude = true;
      }

      if (shouldInclude && !addedDates.has(dateStr)) {
        addedDates.add(dateStr);
        const exception = recurringEvent.exceptions?.[dateStr];

        if (!exception?.cancelled) {
          const occurrence = {
            ...recurringEvent,
            date: new Date(currentDate),
            startTime:
              exception?.modified?.startTime || recurringEvent.startTime,
            endTime: exception?.modified?.endTime || recurringEvent.endTime,
            title: exception?.modified?.title || recurringEvent.title,
            location: exception?.modified?.location || recurringEvent.location,
            isException: !!exception,
          };
          occurrences.push(occurrence);
        }
      }

      // Increment date based on frequency
      switch (recurringEvent.frequency) {
        case "weekly":
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case "monthly":
          // For monthly events, we need to maintain the same week of the month
          const currentDay = currentDate.getDate();
          const currentMonth = currentDate.getMonth();
          const isLastOfMonth = isLastDayOfType(currentDate, targetDayOfWeek);

          if (isLastOfMonth) {
            // For last-of-month events, find the last occurrence in the next month
            const nextMonth = (currentMonth + 1) % 12;
            const nextMonthYear =
              currentDate.getFullYear() + Math.floor((currentMonth + 1) / 12);

            // Start from the last day of the next month
            const lastDay = new Date(nextMonthYear, nextMonth + 1, 0);
            const lastDayOfWeek = lastDay.getDay();

            // Calculate days to go back to find the last targetDayOfWeek
            const daysToSubtract = (lastDayOfWeek - targetDayOfWeek + 7) % 7;

            // Set to the last occurrence of target day in the next month
            currentDate = new Date(lastDay);
            currentDate.setDate(lastDay.getDate() - daysToSubtract);
          } else {
            // Move to next month, same date
            currentDate.setMonth(currentMonth + 1);

            // If the day doesn't exist in the new month (e.g., trying to get Feb 30)
            // we need to find the appropriate day in the new month
            if (currentDate.getMonth() !== (currentMonth + 1) % 12) {
              // Recalculate the correct day based on the week of month
              const originalDate = new Date(eventStart);
              const firstDayOfOriginalMonth = new Date(
                originalDate.getFullYear(),
                originalDate.getMonth(),
                1
              );
              const firstDayWeekday = firstDayOfOriginalMonth.getDay();
              const originalDay = originalDate.getDate();
              const originalWeekdayPosition = Math.ceil(
                (originalDay +
                  firstDayWeekday -
                  ((7 + targetDayOfWeek - firstDayWeekday) % 7)) /
                  7
              );

              // Calculate for the new month
              const newMonth = (currentMonth + 1) % 12;
              const newYear =
                currentDate.getFullYear() + Math.floor((currentMonth + 1) / 12);
              const firstDayOfNewMonth = new Date(newYear, newMonth, 1);
              const daysUntilFirst =
                (7 + targetDayOfWeek - firstDayOfNewMonth.getDay()) % 7;
              currentDate = new Date(newYear, newMonth, 1 + daysUntilFirst);
              currentDate.setDate(
                currentDate.getDate() + (originalWeekdayPosition - 1) * 7
              );
            }
          }
          break;
      }

      iterationCount++;
    }

    if (iterationCount >= MAX_ITERATIONS) {
      console.warn("Max iterations reached in occurrence generation");
    }

    return occurrences;
  };

  const fetchFormSubmissions = async (date, attachedFormId) => {
    if (!organizationId || !attachedFormId) return [];

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // First, get all residents
    const residentsRef = collection(
      db,
      `organizations/${organizationId}/residents`
    );
    const residentsSnapshot = await getDocs(residentsRef);

    // Array to hold all submissions
    let allSubmissions = [];

    // For each resident, check their formSubmissions subcollection
    const fetchPromises = residentsSnapshot.docs.map(async (residentDoc) => {
      const residentId = residentDoc.id;

      const submissionsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentId}/formSubmissions`
      );

      const q = query(
        submissionsRef,
        where("formId", "==", attachedFormId),
        where("dateSubmitted", ">=", startOfDay),
        where("dateSubmitted", "<=", endOfDay)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        residentId, // Include the residentId for reference
        ...doc.data(),
      }));
    });

    // Wait for all promises to resolve
    const submissionsArrays = await Promise.all(fetchPromises);

    // Flatten the array of arrays into a single array
    allSubmissions = submissionsArrays.flat();

    return allSubmissions;
  };

  useEffect(() => {
    const fetchForms = async () => {
      if (!organizationId || !showEventFormModal) return;

      try {
        const formsRef = collection(
          db,
          `organizations/${organizationId}/forms`
        );
        const formsSnapshot = await getDocs(formsRef);
        const forms = formsSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          ...doc.data(),
        }));

        setAvailableForms(forms);
      } catch (error) {
        console.error("Error fetching forms:", error);
      }
    };

    fetchForms();
  }, [organizationId, showEventFormModal]);

  const handleCreateEvent = async (eventData) => {
    try {
      const { startDate, endDate, startTime, endTime, frequency, ...rest } =
        eventData;

      // Validate dates
      const start = new Date(startDate);
      const end = frequency ? new Date(endDate) : new Date(startDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error("Invalid date format");
      }

      // Ensure end date is not before start date
      if (end < start) {
        throw new Error("End date cannot be before start date");
      }

      // Create base event document with validated dates
      const eventRef = await addDoc(
        collection(db, `organizations/${organizationId}/schedule`),
        {
          ...rest,
          startDate: start,
          endDate: end,
          startTime: startTime || "09:00",
          endTime: endTime || "10:00",
          frequency,
          dayOfWeek: start.getDay(), // Always set dayOfWeek
          exceptions: {},
          createdAt: new Date(),
        }
      );

      onAddEvent({ id: eventRef.id, ...eventData });
      handleCloseEventFormModal();
    } catch (error) {
      console.error("Error creating event:", error);
      alert(error.message || "Failed to create event. Please try again.");
    }
  };

  const getRecurrenceText = (frequency) => {
    switch (frequency) {
      case "weekly":
        return "Repeats weekly";
      case "biweekly":
        return "Repeats every 2 weeks";
      case "monthly":
        return "Repeats monthly on the same week";
      default:
        return "";
    }
  };

  // Event updates
  const handleUpdateEvent = async (eventId, changes, updateType = "single") => {
    try {
      const eventRef = doc(
        db,
        `organizations/${organizationId}/schedule`,
        eventId
      );

      const effectiveUpdateType =
        changes.frequency === null ? "futureAll" : updateType;

      if (effectiveUpdateType === "single") {
        const dateStr = format(changes.date, "yyyy-MM-dd");

        await updateDoc(eventRef, {
          [`exceptions.${dateStr}.modified`]: {
            startTime: changes.startTime,
            endTime: changes.endTime,
            title: changes.title,
            location: changes.location,
          },
        });
      } else if (effectiveUpdateType === "futureAll") {
        const newEndDate = new Date(changes.startDate);
        if (changes.frequency) {
          newEndDate.setFullYear(newEndDate.getFullYear() + 3);
        }

        const updateData = {
          title: changes.title,
          type: changes.type,
          location: changes.location,
          startTime: changes.startTime,
          endTime: changes.endTime,
          frequency: changes.frequency,
          startDate: new Date(changes.startDate),
          dayOfWeek: new Date(changes.startDate).getDay(),
          endDate: changes.frequency ? newEndDate : changes.startDate,
          updatedAt: new Date(),
        };

        await updateDoc(eventRef, updateData);
      }

      onUpdateEvent({ id: eventId, ...changes });
      handleCloseEventFormModal();
      setShowRecurringConfirm(false);
    } catch (error) {
      console.error("Error updating event:", error);
      alert("Failed to update event. Please try again.");
    }
  };

  // Event deletion
  const handleDeleteEvent = async (eventId, date, deleteType = "single") => {
    try {
      const eventRef = doc(
        db,
        `organizations/${organizationId}/schedule`,
        eventId
      );

      if (deleteType === "single") {
        // Mark this occurrence as cancelled
        const dateStr = format(date, "yyyy-MM-dd");
        await updateDoc(eventRef, {
          [`exceptions.${dateStr}.cancelled`]: true,
        });
      } else if (deleteType === "futureAll") {
        // Update the end date to the day before this occurrence
        const newEndDate = new Date(date);
        newEndDate.setDate(newEndDate.getDate() - 1);
        await updateDoc(eventRef, {
          endDate: newEndDate,
        });
      } else if (deleteType === "all") {
        // Delete the entire series
        await deleteDoc(eventRef);
      }

      onDeleteEvent(eventId);
      handleCloseEventFormModal();
      setShowDeleteRecurringConfirm(false);
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event. Please try again.");
    }
  };

  const handleCellClick = async (date) => {
    const dayEvents = getDayEvents(date);
    const dayCheckIns = getDayCheckIns(date);
    const dayOvernights = getDayOvernights(date);

    // Fetch submissions for each event that has an attached form
    const submissionsByEvent = await Promise.all(
      dayEvents
        .filter((event) => event.hasAttachedForm)
        .map(async (event) => {
          const submissions = await fetchFormSubmissions(
            date,
            event.attachedFormId
          );
          return {
            eventId: event.id,
            formName: event.attachedFormName,
            submissionCount: submissions.length,
            submissions,
          };
        })
    );

    setSelectedDate(date);
    setSelectedDateEvents(dayEvents);
    setSelectedDateCheckIns(dayCheckIns);
    setSelectedDateOvernights(dayOvernights);
    setSelectedDateSubmissions(submissionsByEvent);
    setShowDayModal(true);
  };

  // UI Event Handlers
  const handleAddEventClick = () => {
    const baseDate = new Date();
    baseDate.setHours(9, 0, 0, 0);

    const endDate = new Date(baseDate);
    endDate.setHours(10, 0, 0, 0);

    setCurrentEvent({
      ...defaultEventState,
      startDate: baseDate,
      endDate: endDate,
      startTime: "09:00",
      endTime: "10:00",
    });
    setEditMode(false);
    setShowEventFormModal(true);
  };

  const handleEventClick = (event) => {
    // Ensure we have valid dates before creating Date objects
    const startDateTime = event.startTime
      ? `${format(event.date || event.startDate, "yyyy-MM-dd")}T${
          event.startTime
        }`
      : format(new Date(), "yyyy-MM-dd'T'HH:mm");

    const endDateTime = event.endTime
      ? `${format(event.date || event.startDate, "yyyy-MM-dd")}T${
          event.endTime
        }`
      : format(new Date(), "yyyy-MM-dd'T'HH:mm");

    setCurrentEvent({
      ...event,
      startDate: new Date(startDateTime),
      endDate: new Date(endDateTime),
      // Preserve the original times
      startTime: event.startTime || "09:00",
      endTime: event.endTime || "10:00",
    });
    setEditMode(true);
    setShowEventFormModal(true);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();

    // Validate times
    if (currentEvent.endDate <= currentEvent.startDate) {
      alert("End time must be after start time");
      return;
    }

    if (editMode) {
      // If we're switching from recurring to non-recurring
      if (!currentEvent.frequency && pendingChanges?.frequency) {
        setPendingChanges(currentEvent);
        setShowRecurringConfirm(true);
        return;
      }

      if (currentEvent.frequency) {
        // Show confirmation for recurring event update
        setPendingChanges(currentEvent);
        setShowRecurringConfirm(true);
      } else {
        await handleUpdateEvent(currentEvent.id, {
          ...currentEvent,
          endDate: currentEvent.startDate, // Set end date to start date for non-recurring events
        });
      }
    } else {
      await handleCreateEvent(currentEvent);
    }
  };

  // Helper function to get events for a specific day
  const getDayEvents = (date) => {
    return events
      .filter((event) => {
        const eventDate = event.date || event.startDate;
        const parsedEventDate = new Date(eventDate);
        return (
          parsedEventDate.getDate() === date.getDate() &&
          parsedEventDate.getMonth() === date.getMonth() &&
          parsedEventDate.getFullYear() === date.getFullYear()
        );
      })
      .sort((a, b) => {
        const aTime = new Date(`${format(date, "yyyy-MM-dd")}T${a.startTime}`);
        const bTime = new Date(`${format(date, "yyyy-MM-dd")}T${b.startTime}`);
        return aTime - bTime;
      });
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const handleCloseDayModal = () => {
    setShowDayModal(false);
    setSelectedDate(null);
    setSelectedDateEvents([]);
  };

  const handleCloseEventFormModal = () => {
    setShowEventFormModal(false);
    setEditMode(false);
    setCurrentEvent(defaultEventState);
  };

  const getEventColor = (type) => {
    switch (type) {
      case "Mandatory":
        return "bg-red-500/20 text-red-200 hover:bg-red-500/30 border-red-500/30";
      case "House":
        return "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border-emerald-500/30";
      case "AA":
        return "bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 border-blue-500/30";
      case "NA":
        return "bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 border-purple-500/30";
      case "meeting":
        return "bg-green-500/20 text-green-200 hover:bg-green-500/30 border-green-500/30";
      case "overnight":
        return "bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 border-purple-500/30";
      case "other":
        return "bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 border-blue-500/30";
      case "CheckIn": // Legacy for backward compatibility
        return "bg-yellow-500/20 text-yellow-200 hover:bg-yellow-500/30 border-yellow-500/30";
      default:
        return "bg-slate-500/20 text-slate-200 hover:bg-slate-500/30 border-slate-500/30";
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case "House":
        return "🏠";
      case "AA":
        return "👥";
      case "NA":
        return "🤝";
      case "CheckIn":
        return "✅";
      default:
        return "📅";
    }
  };

  const firstDayIndex = getDay(startOfMonth(currentMonth));
  const paddingDays = Array.from({ length: firstDayIndex }, (_, i) => null);
  const allDays = [
    ...paddingDays,
    ...eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    }),
  ];

  if (isLoading) return <Spinner />;

  return (
    <div className="w-full h-full">
      {/* Mobile-optimized header */}
      <div className="flex items-center justify-between mb-2 sm:mb-6 px-1 sm:px-4">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <CalendarIcon className="w-5 h-5 sm:w-8 sm:h-8 text-blue-400" />
          <h2 className="text-lg sm:text-2xl font-bold text-white tracking-tight">
            {format(currentMonth, "MMMM yyyy")}
          </h2>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          {canManageSchedule && (
            <button
              onClick={handleAddEventClick}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-xs sm:text-base"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Add Event</span>
              <span className="sm:hidden">Add</span>
            </button>
          )}
          <button
            onClick={() => setCurrentMonth(new Date())}
            className="hidden sm:block px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors"
          >
            Today
          </button>
          <div className="flex rounded-lg bg-slate-800/50 p-0.5 sm:p-1">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="p-1.5 sm:p-2 hover:bg-slate-700 rounded-md transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-slate-400" />
            </button>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="p-1.5 sm:p-2 hover:bg-slate-700 rounded-md transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-px bg-slate-700/20 rounded-lg overflow-hidden shadow-xl border border-slate-700/50 mx-0 sm:mx-4">
        {/* Day headers - Smaller on mobile */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="bg-slate-800/80 p-1 sm:px-2 sm:py-3 text-center"
          >
            <span className="text-xs sm:text-sm font-medium text-slate-400">
              {day.charAt(0)}
            </span>
          </div>
        ))}

        {/* Calendar cells */}
        {allDays.map((date, idx) => {
          if (date === null) {
            return (
              <div
                key={idx}
                className="bg-slate-800/30 min-h-[60px] sm:min-h-[120px] p-1 sm:p-2"
              ></div>
            );
          }

          const dayEvents = getDayEvents(date);
          const dayCheckIns = getDayCheckIns(date);
          const dayOvernights = getDayOvernights(date);
          const isCurrentMonth = isSameMonth(date, currentMonth);
          const isSelected =
            selectedDate && date.toDateString() === selectedDate.toDateString();

          return (
            <div
              key={idx}
              onClick={() => handleCellClick(date)}
              className={`min-h-[120px] bg-slate-800/80 p-2 transition-colors relative group cursor-pointer
                ${!isCurrentMonth ? "bg-slate-800/30" : "hover:bg-slate-700/50"}
                ${isToday(date) ? "ring-2 ring-blue-500/50 ring-inset" : ""}
                ${isSelected ? "ring-2 ring-blue-500 ring-inset" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex h-4 w-4 sm:h-6 sm:w-6 items-center justify-center rounded-full text-[10px] sm:text-sm
      ${isToday(date) ? "bg-blue-500 text-white" : ""}
      ${isCurrentMonth ? "text-slate-300" : "text-slate-600"}`}
                >
                  {format(date, "d")}
                </span>
              </div>
              <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                {!isResidentDashboard && dayOvernights.length > 0 && (
                  <div className="px-1 sm:px-2 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-pink-500/20 text-pink-200 hover:bg-pink-500/30 border-pink-500/30 truncate">
                    {dayOvernights.length}{" "}
                    <span className="hidden sm:inline">
                      Overnight{dayOvernights.length !== 1 ? "s" : ""}
                    </span>
                    <span className="sm:hidden">O</span>
                  </div>
                )}
                {!isResidentDashboard && dayCheckIns.length > 0 && (
                  <div className="px-1 sm:px-2 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-green-500/20 text-green-200 hover:bg-green-500/30 border-green-500/30 truncate">
                    {dayCheckIns.length}{" "}
                    <span className="hidden sm:inline">
                      {/* Change the text here from "Meeting" to "Check In" */}
                      Check In{dayCheckIns.length !== 1 ? "s" : ""}
                    </span>
                    <span className="sm:hidden">C</span>
                  </div>
                )}
                {dayEvents.slice(0, 2).map((event, i) => (
                  <div
                    key={i}
                    className={`px-1 sm:px-2 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium truncate ${getEventColor(
                      event.type
                    )}`}
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Day View Modal */}
      {showDayModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-0 sm:p-2">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-xl w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header - Fixed at top */}
            <div className="sticky top-0 px-3 py-2 sm:px-4 sm:py-3 border-b border-slate-800 flex items-center justify-between bg-slate-900 z-10">
              <h3 className="text-sm sm:text-base font-semibold text-white flex items-center space-x-2">
                <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                <span className="truncate">
                  {format(selectedDate, "EEE, MMM d, yyyy")}
                </span>
              </h3>
              <button
                onClick={handleCloseDayModal}
                className="p-1 sm:p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-2 sm:p-3">
              <div className="space-y-2 sm:space-y-3">
                {/* Form Submissions Section */}
                {!isResidentDashboard && selectedDateSubmissions.length > 0 && (
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-slate-400 mb-1 sm:mb-1.5">
                      Form Submissions
                    </h4>
                    <div className="grid gap-1 sm:gap-2">
                      {selectedDateSubmissions.map((event) => (
                        <div
                          key={event.eventId}
                          className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-1.5 sm:p-2 flex flex-col sm:flex-row justify-between items-start sm:items-center"
                        >
                          <div>
                            <p className="text-blue-200 font-medium text-xs sm:text-sm">
                              {event.formName}
                            </p>
                            <p className="text-blue-300/70 text-xs">
                              {event.submissions.length} submission
                              {event.submissions.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              setViewingSubmissions(event.submissions)
                            }
                            className="mt-1 sm:mt-0 px-2 py-0.5 sm:px-3 sm:py-1 bg-blue-500/30 hover:bg-blue-500/50 text-white rounded-lg text-xs"
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Overnight requests section */}
                {!isResidentDashboard && selectedDateOvernights.length > 0 && (
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-slate-400 mb-1 sm:mb-1.5">
                      Overnight Requests
                    </h4>
                    <div className="grid gap-1 sm:gap-1.5">
                      {selectedDateOvernights.map((overnight) => (
                        <div
                          key={overnight.id}
                          className="bg-pink-500/20 border border-pink-500/30 rounded-lg p-1.5 sm:p-2 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-1 sm:space-y-0"
                        >
                          {/* Resident Information */}
                          <div className="flex items-start space-x-1.5 sm:space-x-2 w-full sm:w-auto">
                            <div className="h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-pink-500/20 flex items-center justify-center shrink-0">
                              <span className="text-pink-200 text-xs font-medium">
                                {overnight.residentName
                                  ?.charAt(0)
                                  ?.toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-pink-200 font-medium text-xs">
                                {overnight.residentName}
                              </p>
                              <p className="text-pink-300/70 text-xs truncate">
                                {overnight.location}
                              </p>
                              {overnight.house && (
                                <p className="text-pink-300/70 text-xs">
                                  House: {overnight.house}
                                </p>
                              )}
                              {overnight.contingencyPlan && (
                                <p className="text-pink-300/70 text-xs">
                                  Plan: {overnight.contingencyPlan}
                                </p>
                              )}
                            </div>
                          </div>
                          {/* Date Information */}
                          <div className="text-right text-xs shrink-0 w-full sm:w-auto">
                            <p className="text-pink-300/70">
                              {format(
                                new Date(
                                  overnight.startDate
                                    .toISOString()
                                    .split("T")[0] + "T12:00:00"
                                ),
                                "MMM d"
                              )}{" "}
                              -{" "}
                              {format(
                                new Date(
                                  overnight.endDate
                                    .toISOString()
                                    .split("T")[0] + "T12:00:00"
                                ),
                                "MMM d"
                              )}
                            </p>
                            <p className="text-pink-200">
                              Returns{" "}
                              {format(
                                new Date(
                                  overnight.endDate
                                    .toISOString()
                                    .split("T")[0] + "T12:00:00"
                                ),
                                "MMM d"
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Check-ins section */}
                {!isResidentDashboard && selectedDateCheckIns.length > 0 && (
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-slate-400 mb-1 sm:mb-1.5">
                      Check-ins
                    </h4>
                    <div className="grid gap-1 sm:gap-1.5">
                      {selectedDateCheckIns.map((checkIn) => {
                        // Determine if this meeting check-in is valid (45+ minutes)
                        let isValidMeeting = true; // Default to true for non-meeting types

                        if (checkIn.type === "meeting") {
                          // For meeting check-ins, check if they meet the minimum duration requirement
                          isValidMeeting =
                            checkIn.meetsMinimumDuration === true;

                          // Debug logging for this specific check-in
                          console.log(
                            `Displaying ${checkIn.residentName}: Duration=${checkIn.duration}, meetsMinimumDuration=${checkIn.meetsMinimumDuration}, isValidMeeting=${isValidMeeting}`
                          );
                        }

                        // Determine colors based on check-in type and validity - SIMPLIFIED LOGIC
                        let colorClasses;

                        if (checkIn.type === "meeting") {
                          if (isValidMeeting) {
                            // Valid meeting (45+ minutes) - Green
                            colorClasses = {
                              bg: "bg-green-500/20",
                              border: "border-green-500/30",
                              text: "text-green-200",
                              textFaded: "text-green-300/70",
                            };
                          } else {
                            // Invalid meeting (under 45 minutes) - Red
                            colorClasses = {
                              bg: "bg-red-500/20",
                              border: "border-red-500/30",
                              text: "text-red-200",
                              textFaded: "text-red-300/70",
                            };
                          }
                        } else if (checkIn.type === "overnight") {
                          colorClasses = {
                            bg: "bg-purple-500/20",
                            border: "border-purple-500/30",
                            text: "text-purple-200",
                            textFaded: "text-purple-300/70",
                          };
                        } else {
                          colorClasses = {
                            bg: "bg-blue-500/20",
                            border: "border-blue-500/30",
                            text: "text-blue-200",
                            textFaded: "text-blue-300/70",
                          };
                        }

                        return (
                          <div
                            key={checkIn.id}
                            className={`${colorClasses.bg} border ${colorClasses.border} rounded-lg p-1.5 sm:p-2 flex flex-col sm:flex-row items-start sm:items-center justify-between`}
                          >
                            <div className="flex items-start space-x-1.5 sm:space-x-2">
                              <div
                                className={`h-5 w-5 sm:h-6 sm:w-6 rounded-full ${colorClasses.bg} flex items-center justify-center shrink-0`}
                              >
                                <span className={colorClasses.text}>
                                  {checkIn.residentName
                                    ?.charAt(0)
                                    ?.toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p
                                  className={`${colorClasses.text} font-medium text-xs`}
                                >
                                  {checkIn.residentName}
                                </p>
                                <p
                                  className={`${colorClasses.textFaded} text-xs`}
                                >
                                  {checkIn.type === "meeting"
                                    ? "Meeting"
                                    : checkIn.type === "overnight"
                                    ? "Overnight"
                                    : "Other"}
                                  : {checkIn.meetingName || "Check-in"}
                                  {/* Add duration info for meetings */}
                                  {checkIn.type === "meeting" &&
                                    checkIn.duration !== null &&
                                    checkIn.duration !== undefined && (
                                      <span
                                        className={`ml-1 font-medium ${
                                          isValidMeeting
                                            ? "text-green-400"
                                            : "text-red-400"
                                        }`}
                                      >
                                        ({Math.round(checkIn.duration)}min
                                        {!isValidMeeting
                                          ? " - Too Short"
                                          : " - Valid"}
                                        )
                                      </span>
                                    )}
                                </p>
                                {checkIn.location?.address && (
                                  <p
                                    className={`${colorClasses.textFaded} text-xs flex items-center`}
                                  >
                                    <MapPin className="w-3 h-3 mr-1" />
                                    {checkIn.location.address}
                                  </p>
                                )}

                                {/* Add weekly meeting count badge for meeting check-ins */}
                                {checkIn.type === "meeting" && (
                                  <div
                                    className={`mt-1 flex items-center ${colorClasses.text}`}
                                  >
                                    <span className="text-xs mr-1">
                                      Weekly meetings:
                                    </span>
                                    <span
                                      className={`px-2 py-1 rounded-full font-medium text-xs ${
                                        isValidMeeting
                                          ? "bg-green-500/20 border border-green-500/30 text-green-200"
                                          : "bg-red-500/20 border border-red-500/30 text-red-200"
                                      }`}
                                    >
                                      {checkIn.weeklyMeetingCount || 0}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div
                              className={`${colorClasses.textFaded} text-xs mt-1 sm:mt-0 w-full sm:w-auto text-right`}
                            >
                              {format(checkIn.timestamp, "h:mm a")}
                              {checkIn.hasCheckOut && checkIn.checkOutTime && (
                                <> - {format(checkIn.checkOutTime, "h:mm a")}</>
                              )}
                              <div
                                className={`mt-1 text-center ${colorClasses.text} text-xs px-1.5 py-0.5 rounded`}
                              >
                                {!checkIn.hasCheckOut
                                  ? "Active"
                                  : checkIn.type === "meeting" &&
                                    !isValidMeeting
                                  ? "Invalid"
                                  : "Valid"}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Events section */}
                <div>
                  <div className="flex items-center justify-between mb-1 sm:mb-1.5">
                    <h4 className="text-xs sm:text-sm font-medium text-slate-400">
                      Events
                    </h4>
                    {canManageSchedule && (
                      <button
                        onClick={() => {
                          const baseDate = new Date(selectedDate);
                          baseDate.setHours(9, 0, 0, 0);
                          setCurrentEvent({
                            ...defaultEventState,
                            startDate: baseDate,
                            endDate: baseDate,
                            startTime: "09:00",
                            endTime: "10:00",
                          });
                          setEditMode(false);
                          setShowEventFormModal(true);
                          setShowDayModal(false);
                        }}
                        className="flex items-center space-x-1 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-xs"
                      >
                        <Plus className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                        <span>Add</span>
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    {selectedDateEvents.length === 0 ? (
                      <p className="text-slate-400 text-center py-2 text-xs">
                        No events scheduled
                      </p>
                    ) : (
                      selectedDateEvents.map((event, index) => {
                        const isPastEvent = new Date(
                          event.date || event.startDate
                        );
                        new Date().setHours(0, 0, 0, 0);
                        const hasForm =
                          event.hasAttachedForm && event.attachedFormId;
                        const canOpenForm =
                          isPastEvent &&
                          hasForm &&
                          typeof onOpenForm === "function" &&
                          isResidentDashboard;

                        return (
                          <div
                            key={index}
                            className={`${getEventColor(event.type)}
                  p-1.5 sm:p-2 rounded-lg border group relative
                  ${canOpenForm ? "cursor-pointer" : ""}`}
                            onClick={() => {
                              if (canOpenForm) {
                                onOpenForm(event);
                                handleCloseDayModal();
                              }
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="space-y-0.5 min-w-0 flex-1">
                                <div className="flex items-center space-x-1">
                                  <span>{getEventTypeIcon(event.type)}</span>
                                  <h4 className="font-medium text-xs truncate">
                                    {event.title}
                                  </h4>
                                  {canOpenForm && (
                                    <span className="text-xs bg-blue-500/40 px-1 py-0.5 rounded shrink-0">
                                      Form
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1 text-xs opacity-80">
                                  <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
                                  <span className="whitespace-nowrap">
                                    {format(
                                      new Date(
                                        `2000-01-01T${
                                          event.startTime || "09:00"
                                        }`
                                      ),
                                      "h:mm a"
                                    )}{" "}
                                    -
                                    {format(
                                      new Date(
                                        `2000-01-01T${event.endTime || "10:00"}`
                                      ),
                                      "h:mm a"
                                    )}
                                  </span>
                                </div>
                                {event.location.name && (
                                  <div className="flex items-center space-x-1 text-xs opacity-80 truncate">
                                    <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
                                    <span className="truncate">
                                      {event.location.name}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {canManageSchedule && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEventClick(event);
                                  }}
                                  className="p-1 hover:bg-slate-700/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-1"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Form Modal */}
      {showEventFormModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5 text-blue-400" />
                <span>{currentEvent.id ? "Edit Event" : "New Event"}</span>
              </h3>
              <button
                onClick={handleCloseEventFormModal}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleSaveEvent} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1.5">
                      Title
                    </label>
                    <input
                      type="text"
                      value={currentEvent.title}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          title: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white placeholder-slate-500"
                      placeholder="Enter event title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1.5">
                      Type
                    </label>
                    <select
                      value={currentEvent.type}
                      onChange={(e) =>
                        setCurrentEvent({
                          ...currentEvent,
                          type: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white appearance-none"
                    >
                      <option value="Mandatory">Mandatory Meeting</option>
                      <option value="House">House Meeting</option>
                      <option value="AA">AA Meeting</option>
                      <option value="NA">NA Meeting</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1.5">
                      Location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        value={currentEvent.location.name}
                        onChange={(e) =>
                          setCurrentEvent({
                            ...currentEvent,
                            location: {
                              ...currentEvent.location,
                              name: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white placeholder-slate-500"
                        placeholder="Enter location"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">
                        Start Time
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="datetime-local"
                          value={
                            currentEvent.startDate instanceof Date &&
                            !isNaN(currentEvent.startDate)
                              ? format(
                                  currentEvent.startDate,
                                  "yyyy-MM-dd'T'HH:mm"
                                )
                              : format(new Date(), "yyyy-MM-dd'T'HH:mm")
                          }
                          onChange={(e) => {
                            const date = new Date(e.target.value);
                            setCurrentEvent({
                              ...currentEvent,
                              startDate: date,
                              dayOfWeek: date.getDay(),
                              startTime: format(date, "HH:mm"),
                              // Only update end date if not recurring
                              ...(currentEvent.frequency === null && {
                                endDate: date,
                              }),
                            });
                          }}
                          className="w-full pl-10 pr-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">
                        End Time
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="datetime-local"
                          value={
                            currentEvent.endDate instanceof Date &&
                            !isNaN(currentEvent.endDate)
                              ? format(
                                  currentEvent.endDate,
                                  "yyyy-MM-dd'T'HH:mm"
                                )
                              : format(new Date(), "yyyy-MM-dd'T'HH:mm")
                          }
                          onChange={(e) => {
                            const date = new Date(e.target.value);
                            setCurrentEvent({
                              ...currentEvent,
                              endDate: date,
                              endTime: format(date, "HH:mm"),
                            });
                          }}
                          className="w-full pl-10 pr-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentEvent.frequency !== null}
                        onChange={(e) => {
                          const isRecurring = e.target.checked;
                          let newEndDate = new Date(currentEvent.endDate);

                          if (isRecurring) {
                            // If checking the box, set end date to 3 years from start
                            newEndDate.setFullYear(
                              newEndDate.getFullYear() + 3
                            );
                          } else {
                            // If unchecking, set end date back to start date
                            newEndDate = new Date(currentEvent.startDate);
                          }

                          setCurrentEvent({
                            ...currentEvent,
                            frequency: isRecurring ? "weekly" : null,
                            endDate: newEndDate,
                            // Preserve the original times
                            startTime: currentEvent.startTime,
                            endTime: currentEvent.endTime,
                          });
                        }}
                      />
                      <span className="text-sm font-medium text-slate-300">
                        Recurring Meeting
                      </span>
                    </label>
                  </div>

                  {currentEvent.frequency && (
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">
                        Recurrence Pattern
                      </label>
                      <select
                        value={currentEvent.frequency}
                        onChange={(e) => {
                          const newFrequency = e.target.value;
                          // Keep the same day of week and just update the frequency
                          setCurrentEvent({
                            ...currentEvent,
                            frequency: newFrequency,
                            dayOfWeek: currentEvent.startDate.getDay(), // Ensure we keep the same day
                          });
                        }}
                        className="w-full px-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white appearance-none"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Every 2 Weeks</option>
                        <option value="monthly">Monthly (Same Week)</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="pt-4 border-t border-slate-700/50 mt-4">
                  <div className="flex items-center space-x-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={currentEvent.hasAttachedForm}
                      onChange={(e) => {
                        const hasForm = e.target.checked;
                        setCurrentEvent({
                          ...currentEvent,
                          hasAttachedForm: hasForm,
                          attachedFormId: hasForm
                            ? currentEvent.attachedFormId
                            : null,
                          attachedFormName: hasForm
                            ? currentEvent.attachedFormName
                            : null,
                        });
                      }}
                    />
                    <span className="text-sm font-medium text-slate-300">
                      Attach Form
                    </span>
                  </div>

                  {currentEvent.hasAttachedForm && (
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1.5">
                        Select Form
                      </label>
                      <select
                        value={currentEvent.attachedFormId || ""}
                        onChange={(e) => {
                          const formId = e.target.value;
                          const selectedForm = availableForms.find(
                            (f) => f.id === formId
                          );
                          setCurrentEvent({
                            ...currentEvent,
                            attachedFormId: formId,
                            attachedFormName: selectedForm
                              ? selectedForm.name
                              : null,
                          });
                        }}
                        className="w-full px-3 py-2.5 bg-slate-800/50 rounded-lg border border-slate-700 text-white appearance-none"
                      >
                        <option value="">-- Select a form --</option>
                        {availableForms.map((form) => (
                          <option key={form.id} value={form.id}>
                            {form.name}
                          </option>
                        ))}
                      </select>

                      <div className="mt-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              currentEvent.frequency !== null &&
                              currentEvent.hasAttachedForm
                            }
                            disabled={!currentEvent.hasAttachedForm}
                            onChange={(e) => {
                              // Only change if a form is attached
                              if (!currentEvent.hasAttachedForm) return;

                              const isRecurring = e.target.checked;
                              let newEndDate = new Date(currentEvent.endDate);

                              if (isRecurring) {
                                // If checking the box, set end date to 3 years from start
                                newEndDate.setFullYear(
                                  newEndDate.getFullYear() + 3
                                );
                              } else {
                                // If unchecking, set end date back to start date
                                newEndDate = new Date(currentEvent.startDate);
                              }

                              setCurrentEvent({
                                ...currentEvent,
                                frequency: isRecurring ? "weekly" : null,
                                endDate: newEndDate,
                                // Preserve the original times
                                startTime: currentEvent.startTime,
                                endTime: currentEvent.endTime,
                              });
                            }}
                          />
                          <span className="text-sm font-medium text-slate-300">
                            Make this a recurring form
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end items-center space-x-3 pt-6">
                  {currentEvent.id && (
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteEvent(
                          currentEvent.id,
                          currentEvent.date,
                          "all"
                        )
                      }
                      className="px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-2"
                  >
                    {currentEvent.id ? (
                      <>
                        <Edit2 className="w-4 h-4" />
                        <span>Update</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Create</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showRecurringConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Update Recurring Event
            </h3>
            <p className="text-slate-300 mb-6">
              Would you like to apply these changes to all future occurrences of
              this event?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  handleUpdateEvent(currentEvent.id, currentEvent, "single")
                }
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                This event only
              </button>
              <button
                onClick={() =>
                  handleUpdateEvent(currentEvent.id, currentEvent, "futureAll")
                }
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                All future events
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Recurring Confirmation Modal */}
      {showDeleteRecurringConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Delete Recurring Event
            </h3>
            <p className="text-slate-300 mb-6">
              Would you like to delete only this occurrence or this and all
              future occurrences?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  handleDeleteEvent(
                    currentEvent.id,
                    currentEvent.date,
                    "single"
                  )
                }
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                This event only
              </button>
              <button
                onClick={() =>
                  handleDeleteEvent(
                    currentEvent.id,
                    currentEvent.date,
                    "futureAll"
                  )
                }
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                All future events
              </button>
            </div>
          </div>
        </div>
      )}
      {viewingSubmissions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Form Submissions
              </h3>
              <button
                onClick={() => setViewingSubmissions(null)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {viewingSubmissions.length === 0 ? (
                <p className="text-slate-400 text-center">
                  No submissions found.
                </p>
              ) : (
                viewingSubmissions.map((submission, index) => (
                  <div key={index} className="bg-slate-800/50 p-4 rounded-lg">
                    {/* Clickable Header to Expand/Collapse */}
                    <button
                      onClick={() => toggleSubmission(index)}
                      className="flex items-center justify-between w-full text-white font-medium hover:bg-slate-700 p-2 rounded-lg transition"
                    >
                      <span>
                        {submission.residentFullName || "Unknown Resident"}
                      </span>
                      <span className="text-slate-400 text-sm">
                        {new Date(
                          submission.dateSubmitted.seconds * 1000
                        ).toLocaleString()}
                      </span>
                      <span className="text-slate-400">
                        {expandedSubmissions[index] ? "▲" : "▼"}
                      </span>
                    </button>

                    {/* Expandable Section */}
                    {expandedSubmissions[index] && (
                      <div className="mt-2 space-y-2">
                        {submission.responses &&
                          Object.entries(submission.responses).map(
                            ([field, value], i) => (
                              <div
                                key={i}
                                className="p-2 bg-slate-700/50 rounded-md text-white text-sm"
                              >
                                <strong>
                                  {submission.elementMetadata[field]?.label ||
                                    field}
                                  :
                                </strong>{" "}
                                {typeof value === "boolean"
                                  ? value
                                    ? "Yes"
                                    : "No"
                                  : value}
                              </div>
                            )
                          )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
